import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { usePWA } from "../../Context/PWAContext";

const Layout = ({ children }) => {
  const { showInstallModal, setShowInstallModal, isInstalled, installApp } =
    usePWA();
  const [isFloatingDismissed, setIsFloatingDismissed] = useState(
    sessionStorage.getItem("pwa_floating_dismissed") === "true",
  );
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [showServiceModal, setShowServiceModal] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      const isBelow768 = window.innerWidth < 768;
      setIsSmallScreen(isBelow768);
      setSidebarOpen(!isBelow768);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Show modal once per login session
  useEffect(() => {
    const alreadyShown = sessionStorage.getItem("panServiceModalShown");
    if (!alreadyShown) {
      setShowServiceModal(true);
      sessionStorage.setItem("panServiceModalShown", "true");
    }
  }, []);

  return (
    <div className="flex bg-white min-h-screen">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isSmallScreen={isSmallScreen}
      />
      <div
        className={`flex-1 transform transition-all duration-300 ${
          isSmallScreen
            ? "px-4 w-[100%]"
            : isSidebarOpen
              ? "ml-[17rem] w-[calc(100%-17.5rem)] mr-[15px]"
              : "ml-[6.5rem] w-[calc(100%-7rem)] mr-[15px]"
        }`}
      >
        <Navbar toggleSidebar={toggleSidebar} className="w-[100%]" />
        <main className="py-4 mt-4 w-[100%]">{children}</main>
        <Footer className="w-[100%]" />
      </div>

      {/* Updated Modal – with bigger, animated LIVE UPDATE badge */}
      {showServiceModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
          style={{ zIndex: 999999 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center relative transform transition-all duration-300">
            {/* Bigger Live update badge with up/down animation */}
            <div
              className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold px-5 py-2 rounded-full flex items-center gap-2 shadow-md whitespace-nowrap"
              style={{
                animation: "floatBadge 2s ease-in-out infinite",
              }}
            >
              <span className="relative flex h-3 w-3">
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"
                  style={{ animationDuration: "1.5s" }}
                ></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
              </span>
              <span className="text-sm tracking-wide"> LIVE UPDATE</span>
            </div>

            {/* Main headline */}
            <h3 className="text-3xl font-black text-gray-800 mt-3 mb-2 uppercase tracking-tight">
              LOST YOUR <span className="text-indigo-600">PAN CARD ?</span>
            </h3>

            <p className="text-md font-bold text-gray-600 mb-4">
              FIND PAN SERVICE IS NOW <span className="text-red-800">LIVE</span>
            </p>

            <p className="text-sm text-gray-600 mb-4">
              Enter Aadhaar no & <strong>Get Pan Details</strong> on your
              dashboard instantly!
            </p>

            {/* Two ways to search */}
            <div className="bg-indigo-50 rounded-lg p-3 my-3">
              <p className="text-sm font-medium text-gray-700">
                🔍{" "}
                <span className="font-bold text-indigo-700">
                  Two ways to search:
                </span>
              </p>
              <div className="flex justify-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  📇 Aadhaar (12‑digit)
                </span>
                <span className="flex items-center gap-1">
                  📱 Phone + Name (optional)
                </span>
              </div>
            </div>

            {/* Benefit list */}
            <div className="space-y-2 text-left mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500 text-lg">✓</span>
                <span>
                  <strong>Instant</strong> PAN Number retrieval
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500 text-lg">✓</span>
                <span>Verified against official records</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500 text-lg">✓</span>
                <span>Secure & wallet‑deducted</span>
              </div>
            </div>

            {/* Button */}
            <button
              onClick={() => setShowServiceModal(false)}
              className="group relative w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-md hover:shadow-xl flex items-center justify-center gap-2"
            >
              <span>Got it – Let's Search</span>
              <svg
                className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            <p className="text-xs text-gray-400 mt-4">
              One‑time announcement • Secure & encrypted
            </p>
          </div>
        </div>
      )}

      {/* PWA Manual Install Instructions Modal */}
      {showInstallModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-65 p-4"
          style={{ zIndex: 9999999 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative transform transition-all">
            {/* Close button */}
            <button
              onClick={() => setShowInstallModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="text-center mb-5 mt-2">
              <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-3">
                <svg
                  className="w-8 h-8 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-extrabold text-gray-800">
                Install VheecO
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Get instant native-app like access from your home screen!
              </p>
            </div>

            {/* Platform specific instructions */}
            <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100 text-left text-sm text-gray-700 space-y-3">
              {/iPad|iPhone|iPod/.test(navigator.userAgent) ? (
                <>
                  <p className="font-semibold text-indigo-800 text-center pb-1">
                    iOS Safari Instructions:
                  </p>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      1
                    </span>
                    <span>
                      Open <strong>VheecO</strong> in the{" "}
                      <strong>Safari</strong> browser.
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      2
                    </span>
                    <span>
                      Tap the <strong>Share</strong> button (box icon with
                      up-arrow) in Safari's toolbar.
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      3
                    </span>
                    <span>
                      Scroll down the share sheet and tap{" "}
                      <strong>Add to Home Screen</strong>.
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      4
                    </span>
                    <span>
                      Tap <strong>Add</strong> in the top-right corner to
                      install!
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-semibold text-indigo-800 text-center pb-1">
                    Install Instructions:
                  </p>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      1
                    </span>
                    <span>
                      Click the <strong>Install</strong> buttom above the wallet
                      Section
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      2
                    </span>
                    <span>
                      If the prompt does not appear, click your browser's
                      options menu (three vertical dots in Chrome) in the
                      top-right corner.
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">
                      3
                    </span>
                    <span>
                      Select <strong>Install App</strong> or{" "}
                      <strong>Add to Home Screen</strong>.
                    </span>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setShowInstallModal(false)}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* PWA Floating Bottom-Left Install Icon/Button */}
      {!isInstalled && (
        <div
          className="fixed bottom-10 left-8 z-[9999]"
          style={{
            animation: "pwaFloatIndicator 3s ease-in-out infinite",
          }}
        >
          {/* Waves coming out from the div */}
          <div className="absolute inset-0 rounded-full pwa-wave ring-1" />
          <div className="absolute inset-0 rounded-full pwa-wave ring-2" />
          <div className="absolute inset-0 rounded-full pwa-wave ring-3" />

          <div
            className="relative flex items-center gap-1.5
      text-white px-5 py-5 rounded-full hover:scale-105
      transition-all duration-300 cursor-pointer select-none
      border border-white/15 overflow-hidden"
            style={{
              background: "#2E3A8C",
              boxShadow:
                "0 0 12px rgba(46,58,140,0.4), 0 0 25px rgba(46,58,140,0.2)",
            }}
            onClick={installApp}
          >
            {/* Sunlight wave effect */}
            <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
              <div className="sunlight-wave" />
            </div>

            <svg
              className="relative  h-3.5 animate-bounce shrink-0 text-white"
              style={{ animationDuration: "2s" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>

            <span className="relative text-md font-black tracking-wide pr-0.5 shrink-0">
              Install App
            </span>
          </div>
        </div>
      )}

      {/* Custom animations (added once) */}
      <style jsx>{`
        @keyframes floatBadge {
          0% {
            transform: translateX(-50%) translateY(0px);
          }
          50% {
            transform: translateX(-50%) translateY(-6px);
          }
          100% {
            transform: translateX(-50%) translateY(0px);
          }
        }
        @keyframes pwaFloatIndicator {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
          100% {
            transform: translateY(0px);
          }
        }

        .pwa-wave {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          pointer-events: none;
          z-index: -1;
          background: rgba(46, 58, 140, 0.35);
          box-shadow: 0 0 10px 2px rgba(46, 58, 140, 0.4);
          animation: pwaWavePulse 3s cubic-bezier(0.1, 0.8, 0.3, 1) infinite;
        }

        .ring-1 {
          animation-delay: 0s;
        }

        .ring-2 {
          animation-delay: 1s;
        }

        .ring-3 {
          animation-delay: 2s;
        }

        @keyframes pwaWavePulse {
          0% {
            transform: scale(0.95);
            opacity: 0.8;
          }
          50% {
            opacity: 0.4;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }

        .sunlight-wave {
          position: absolute;
          top: -50%;
          left: -120%;
          width: 60%;
          height: 200%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.08),
            rgba(255, 255, 255, 0.35),
            rgba(255, 255, 255, 0.08),
            transparent
          );
          transform: rotate(25deg);
          animation: sunlightWave 2.8s linear infinite;
        }

        @keyframes sunlightWave {
          0% {
            left: -120%;
          }
          100% {
            left: 220%;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
